import { TimeOffCategory, TimeOffType } from '../../types/timeOff';
import { mix, sbg } from '../../theme/sbgTokens';

/**
 * Quiet categorical chips for the OOO / calendar screens (book §13.3).
 *
 * Categories and time-off types are entities, not states, so they wear the
 * categorical chart palette in a FIXED order — never status colors, which are
 * reserved for true states such as "Pending approval". The two orders line up
 * on purpose so related buckets share a slot:
 *
 *   slot 1 Blue    Vacation/Personal Time · PTO
 *   slot 2 Amber   Medical/Health         · Sick
 *   slot 3 Teal    Work From Home         · WFH
 *   slot 4 Violet  Business Travel
 *   slot 5 Magenta Family Event
 *   slot 6 Olive   Other
 *
 * Every value is a `var(--sbg-…)` token (or a `color-mix` of one), so the
 * chips follow dark mode.
 */
export interface SeriesChipStyle {
  /** Tinted fill: the series color at 14% over the surface. */
  bg: string;
  /** Label color — always the primary text role, never the series color. */
  color: string;
  /** The 6px leading dot / legend swatch: the series color itself. */
  dot: string;
  /** Hairline border: the series color at 28% over the surface. */
  border: string;
  /** Short, sentence-case display label. */
  label: string;
}

const seriesStyle = (series: string, label: string): SeriesChipStyle => ({
  bg: mix(series, 14, sbg.bg.surface),
  color: sbg.text.primary,
  dot: series,
  border: mix(series, 28, sbg.bg.surface),
  label,
});

const neutralStyle = (label: string): SeriesChipStyle => ({
  bg: sbg.status.neutral.bg,
  color: sbg.text.primary,
  dot: sbg.palette.slate[400],
  border: sbg.status.neutral.border,
  label,
});

/** Categories in their fixed palette order (also the legend order). */
export const CATEGORY_ORDER: TimeOffCategory[] = [
  TimeOffCategory.VACATION,
  TimeOffCategory.MEDICAL,
  TimeOffCategory.WFH,
  TimeOffCategory.BUSINESS_TRAVEL,
  TimeOffCategory.FAMILY_EVENT,
  TimeOffCategory.OTHER,
];

const CATEGORY_LABEL: Record<TimeOffCategory, string> = {
  [TimeOffCategory.VACATION]: 'Vacation',
  [TimeOffCategory.MEDICAL]: 'Medical',
  [TimeOffCategory.WFH]: 'Work from home',
  [TimeOffCategory.BUSINESS_TRAVEL]: 'Business travel',
  [TimeOffCategory.FAMILY_EVENT]: 'Family event',
  [TimeOffCategory.OTHER]: 'Other',
};

/** Long, sentence-case labels for pickers ("Vacation or personal time"). */
export const CATEGORY_OPTION_LABEL: Record<TimeOffCategory, string> = {
  [TimeOffCategory.VACATION]: 'Vacation or personal time',
  [TimeOffCategory.MEDICAL]: 'Medical or health',
  [TimeOffCategory.WFH]: 'Work from home',
  [TimeOffCategory.BUSINESS_TRAVEL]: 'Business travel',
  [TimeOffCategory.FAMILY_EVENT]: 'Family event',
  [TimeOffCategory.OTHER]: 'Other',
};

const isCategory = (value: unknown): value is TimeOffCategory =>
  typeof value === 'string' && (CATEGORY_ORDER as string[]).includes(value);

/** The short display label for a category; raw Outlook strings pass through. */
export const categoryLabel = (category?: TimeOffCategory | string | null) => {
  if (!category) return 'Uncategorized';
  return isCategory(category) ? CATEGORY_LABEL[category] : category;
};

/**
 * Chip colors for an OOO/calendar category. Accepts either a
 * `TimeOffCategory` enum or its raw string value (the reconciler uses the
 * enum; the OOO requests list comes from Outlook event analysis and arrives as
 * a raw string). Unknown or missing categories render neutral.
 */
export const categoryChipStyle = (category?: TimeOffCategory | string | null): SeriesChipStyle => {
  if (!isCategory(category)) return neutralStyle(categoryLabel(category));
  return seriesStyle(sbg.chart.series[CATEGORY_ORDER.indexOf(category)], CATEGORY_LABEL[category]);
};

/** Time-off types in their fixed palette order (also the legend order). */
export const TIME_OFF_TYPE_ORDER: TimeOffType[] = [TimeOffType.PTO, TimeOffType.SICK, TimeOffType.WFH];

const TYPE_LABEL: Record<TimeOffType, string> = {
  [TimeOffType.PTO]: 'PTO',
  [TimeOffType.SICK]: 'Sick',
  [TimeOffType.WFH]: 'WFH',
};

/** Long labels for pickers. */
export const TIME_OFF_TYPE_OPTION_LABEL: Record<TimeOffType, string> = {
  [TimeOffType.PTO]: 'PTO',
  [TimeOffType.SICK]: 'Sick time',
  [TimeOffType.WFH]: 'Work from home',
};

const isTimeOffType = (value: unknown): value is TimeOffType =>
  typeof value === 'string' && (TIME_OFF_TYPE_ORDER as string[]).includes(value);

export const timeOffTypeLabel = (type?: TimeOffType | string | null) => {
  if (!type) return 'Unknown type';
  return isTimeOffType(type) ? TYPE_LABEL[type] : type;
};

/** Chip colors for a time-off type (PTO / Sick / WFH). */
export const timeOffTypeChipStyle = (type?: TimeOffType | string | null): SeriesChipStyle => {
  if (!isTimeOffType(type)) return neutralStyle(timeOffTypeLabel(type));
  return seriesStyle(sbg.chart.series[TIME_OFF_TYPE_ORDER.indexOf(type)], TYPE_LABEL[type]);
};

/**
 * Pending / projected marks (book §13.4), drawn so they pass WCAG 1.4.11:
 * full-strength series stripes on the surface (3.6–4.8:1 against white for
 * slots 1–3). A tint-on-tint hatch only reaches ~1.1:1, and a full-strength
 * hatch behind text fails text contrast, so the hatch is only ever used for
 * small caps and swatches, never behind a label.
 */
export const pendingHatch = (color: string) =>
  `repeating-linear-gradient(-60deg, ${color} 0 1.5px, ${sbg.bg.surface} 1.5px 5px)`;

/** The pending outline: 1px dashed in the full series color (≥3:1 on white, subtle and selected cells). */
export const pendingBorder = (style: Pick<SeriesChipStyle, 'dot'>) => `1px dashed ${style.dot}`;

/** A quieter tint for entries on days outside the month on screen. */
export const outsideTint = (style: Pick<SeriesChipStyle, 'dot'>) => mix(style.dot, 8, sbg.bg.surface);
